import type { SearchResult, SourceListItem } from "#/types";

import { useRequireAuth } from "#/feature/auth/use-require-auth";
import { AlertCircle, Check, Filter, Loader2, Plus, Search as SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cx } from "styled-system/css";

import {
  pageStyles,
  headerStyles,
  titleStyles,
  subtitleStyles,
  loadingStyles,
  spinAnimation,
  formStyles,
  searchBarContainerStyles,
  searchInputContainerStyles,
  searchIconStyles,
  searchInputStyles,
  filterButtonStyles,
  searchSubmitBtnStyles,
  dropdownStyles,
  dropdownItemStyles,
  gridStyles,
  sourceTagStyles,
  resultCardStyles,
  imageWrapperStyles,
  imageStyles,
  resultContentStyles,
  imagePlaceholderStyles,
  resultTitleStyles,
  resultMetaWrapperStyles,
  resultMetaStyles,
  inLibraryBadgeStyles,
  addButtonStyles,
  overlayActionStyles,
} from "./search.css";
import { useSearch } from "./use-search";
import { Card } from "#/components/card/card";

export function Search() {
  const allowed = useRequireAuth({ admin: true });
  const {
    query,
    source,
    sources,
    results,
    failedSources,
    searching,
    hasSearched,
    searchedQuery,
    addingUrl,
    setQuery,
    setSource,
    handleSearch,
    addToLibrary,
  } = useSearch();

  if (!allowed) return null;

  return (
    <div className={pageStyles}>
      <SearchHeader />

      <SearchForm
        query={query}
        setQuery={setQuery}
        source={source}
        setSource={setSource}
        sources={sources}
        onSearch={handleSearch}
        isSearching={searching}
      />

      <SearchResults
        results={results}
        failedSources={failedSources}
        isSearching={searching}
        hasSearched={hasSearched}
        searchedQuery={searchedQuery}
        onAdd={addToLibrary}
        addingUrl={addingUrl}
      />
    </div>
  );
}

const SearchHeader = () => (
  <div className={headerStyles}>
    <div>
      <h1 className={titleStyles}>Discover Novels</h1>
    </div>
  </div>
);

interface SearchFormProps {
  query: string;
  setQuery: (value: string) => void;
  source: string;
  setSource: (value: string) => void;
  sources: SourceListItem[];
  onSearch: (event: React.FormEvent) => void;
  isSearching: boolean;
}

const SearchForm = ({
  query,
  setQuery,
  source,
  setSource,
  sources,
  onSearch,
  isSearching,
}: SearchFormProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        filterBtnRef.current &&
        !filterBtnRef.current.contains(target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <form onSubmit={onSearch} className={formStyles}>
      <div className={searchBarContainerStyles}>
        <div className={searchInputContainerStyles}>
          <SearchIcon size={20} className={searchIconStyles} />
          <input
            type="text"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            placeholder="Enter novel title"
            className={searchInputStyles}
          />
        </div>

        <div style={{ position: "relative" }}>
          <button
            ref={filterBtnRef}
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className={filterButtonStyles({ active: source !== "" })}
            title="Filter Source"
          >
            <Filter size={20} />
          </button>
          {showDropdown && (
            <div ref={dropdownRef} className={dropdownStyles}>
              <button
                type="button"
                onClick={() => {
                  setSource("");
                  setShowDropdown(false);
                }}
                className={dropdownItemStyles({ active: source === "" })}
              >
                All Sources
              </button>
              {sources.map((sourceItem) => (
                <button
                  key={sourceItem.meta.source_id}
                  type="button"
                  onClick={() => {
                    setSource(sourceItem.meta.source_id);
                    setShowDropdown(false);
                  }}
                  className={dropdownItemStyles({
                    active: source === sourceItem.meta.source_id,
                  })}
                >
                  {sourceItem.meta.source_id} {sourceItem.meta.needs_extension ? "(Ext)" : ""}
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="submit" disabled={isSearching} className={searchSubmitBtnStyles}>
          {isSearching ? <Loader2 size={20} className={spinAnimation} /> : <SearchIcon size={20} />}
        </button>
      </div>
    </form>
  );
};

interface SearchResultsProps {
  results: SearchResult[];
  failedSources: string[];
  isSearching: boolean;
  hasSearched: boolean;
  searchedQuery: string;
  onAdd: (result: SearchResult) => void;
  addingUrl: string | undefined;
}

const SearchResults = ({
  results,
  failedSources,
  isSearching,
  hasSearched,
  searchedQuery,
  onAdd,
  addingUrl,
}: SearchResultsProps) => {
  if (isSearching && results.length === 0) {
    return <div className={loadingStyles}>Searching...</div>;
  }

  if (results.length === 0 && hasSearched && failedSources.length === 0) {
    return <div className={loadingStyles}>No results found for "{searchedQuery}"</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {failedSources.length > 0 && (
        <div
          style={{
            backgroundColor: "rgba(248, 114, 114, 0.1)",
            color: "var(--colors-error)",
            padding: "0.75rem",
            borderRadius: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "14px",
          }}
        >
          <AlertCircle size={16} />
          <span>Failed to search in: {failedSources.join(", ")}</span>
        </div>
      )}
      <div className={gridStyles}>
        {results.map((result, index) => (
          <SearchResultCard
            key={`${result.url}-${index}`}
            result={result}
            inLibrary={result.in_library}
            onAdd={() => onAdd(result)}
            isAdding={addingUrl === result.url}
          />
        ))}
      </div>
    </div>
  );
};

interface SearchResultCardProps {
  result: SearchResult;
  inLibrary: boolean;
  onAdd: () => void;
  isAdding: boolean;
}

const SearchResultCard = ({ result, inLibrary, onAdd, isAdding }: SearchResultCardProps) => (
  <Card className={resultCardStyles}>
    <a
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "contents", color: "inherit", textDecoration: "none" }}
    >
      <div className={imageWrapperStyles}>
        {result.cover_url ? (
          <img src={result.cover_url} alt="" className={imageStyles} />
        ) : (
          <div className={imagePlaceholderStyles}>
            <SearchIcon size={24} />
          </div>
        )}

        <div className={overlayActionStyles}>
          {inLibrary ? (
            <div className={inLibraryBadgeStyles} title="In Library">
              <Check size={14} />
            </div>
          ) : (
            <div
              role="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isAdding) {
                  onAdd();
                }
              }}
              className={addButtonStyles}
              title="Add to Library"
              aria-disabled={isAdding}
            >
              {isAdding ? <Loader2 size={16} className={spinAnimation} /> : <Plus size={20} />}
            </div>
          )}
        </div>
      </div>

      <div className={resultContentStyles}>
        <span className={sourceTagStyles}>{result.source_id.toUpperCase()}</span>
        <h3 className={resultTitleStyles} title={result.title}>
          {result.title}
        </h3>
        <div className={resultMetaWrapperStyles}>
          {result.authors.length > 0 && <div className={resultMetaStyles}>{result.authors[0]}</div>}
        </div>
      </div>
    </a>
  </Card>
);
